import { forwardRef } from 'react';
import classNames from 'classnames';

const buttonClasses = {
  base: 'font-medium rounded-lg shadow-sm focus:outline-none whitespace-nowrap',
  size: {
    xs: 'px-2.5 py-1.5 text-xs',
    sm: 'px-3 py-2 text-sm leading-4',
    md: 'px-4 py-2 text-sm',
    lg: 'px-4 py-2 text-base',
    xl: 'px-6 py-3 text-base',
  },
  variant: {
    primary: 'border border-transparent text-white bg-blue-06 hover:bg-blue-08',
    secondary:
      'shadow border border-transparent text-grey-10 bg-white hover:text-white hover:bg-blue-06 hover:shadow-md',
    tertiary: 'border-2 border-blue-06 text-blue-06 bg-white hover:shadow-md',
  },
};

export const Button = forwardRef(
  (
    {
      as = 'button',
      children,
      type = 'button',
      variant = 'primary',
      className,
      size = 'md',
      ...props
    },
    ref,
  ) => {
    const Tag = as;

    return (
      <Tag
        ref={ref}
        type={as === 'button' ? type : undefined}
        className={classNames([
          buttonClasses.base,
          buttonClasses.size[size],
          buttonClasses.variant[variant],
          className,
        ])}
        {...props}
      >
        {children}
      </Tag>
    );
  },
);

Button.displayName = 'Button';

export const ButtonExtraSmall = ({ children, ...props }) => {
  return (
    <Button size="xs" {...props}>
      {children}
    </Button>
  );
};

export const ButtonSmall = ({ children, ...props }) => {
  return (
    <Button size="sm" {...props}>
      {children}
    </Button>
  );
};

export const ButtonMedium = ({ children, ...props }) => {
  return (
    <Button size="md" {...props}>
      {children}
    </Button>
  );
};

export const ButtonLarge = ({ children, ...props }) => {
  return (
    <Button size="lg" {...props}>
      {children}
    </Button>
  );
};

export const ButtonExtraLarge = ({ children, ...props }) => {
  return (
    <Button size="xl" {...props}>
      {children}
    </Button>
  );
};

export const SecondaryButtonExtraSmall = ({ children, ...props }) => {
  return (
    <Button variant="secondary" size="xs" {...props}>
      {children}
    </Button>
  );
};

export const SecondaryButtonSmall = ({ children, ...props }) => {
  return (
    <Button variant="secondary" size="sm" {...props}>
      {children}
    </Button>
  );
};

export const SecondaryButtonMedium = ({ children, ...props }) => {
  return (
    <Button variant="secondary" size="md" {...props}>
      {children}
    </Button>
  );
};

export const SecondaryButtonLarge = ({ children, ...props }) => {
  return (
    <Button variant="secondary" size="lg" {...props}>
      {children}
    </Button>
  );
};

export const SecondaryButtonExtraLarge = ({ children, ...props }) => {
  return (
    <Button variant="secondary" size="xl" {...props}>
      {children}
    </Button>
  );
};

const inputClasses = {
  base: 'block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-06 focus:border-blue-06',
  size: {
    xs: '',
    sm: '',
    md: 'py-2 text-sm',
    lg: 'py-2 text-base',
    xl: 'py-3 text-base',
  },
  variant: {
    primary: '',
    secondary: '',
  },
};

export const Input = forwardRef(
  (
    {
      as = 'input',
      type = 'text',
      variant = 'primary',
      className,
      size = 'md',
      ...props
    },
    ref,
  ) => {
    const Tag = as;

    return (
      <Tag
        ref={ref}
        type={type}
        className={classNames([
          inputClasses.base,
          inputClasses.size[size],
          inputClasses.variant[variant],
          className,
        ])}
        {...props}
      />
    );
  },
);

Input.displayName = 'Input';

export const InputSmall = (props) => {
  return <Input {...props} size="sm" />;
};

export const InputMedium = (props) => {
  return <Input {...props} size="md" />;
};

export const InputLarge = (props) => {
  return <Input {...props} size="lg" />;
};

export const InputExtraLarge = (props) => {
  return <Input {...props} size="xl" />;
};
